import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RecordManagement } from "app/class/record-management";
import i18n from "app/config/i18n";
import { useSession } from "app/hooks/useSession";
import { usePlatformTheme } from "app/providers/PlatformThemeProvider";
import { platformThemeCssVariables } from "app/theme/platformTheme";
import { buildFormRuntimeScript, buildFormRuntimeStyles } from "modules/gate/form-runtime";
import { useCurrencyPreferences } from "modules/record/shared/hooks/useCurrencyPreferences";
import { GateRuntimeCurrencyPayload } from "modules/gate/types/gate";

interface FormPreviewProps {
    html: string;
    styleCode?: string;
    scriptCode?: string;
    draftTranslations?: Record<string, string>;
}

interface TranslationRecord {
    text_key?: string;
    text_value?: string;
    language?: string;
}

interface PreviewI18nPayload {
    locale: string;
    fallbackLocale: string;
    messages: Record<string, unknown>;
    messagesByLocale: Record<string, Record<string, unknown>>;
}

type CssVariableMap = Record<string, string | number>;

const VUE_CDN_URL = "https://unpkg.com/vue@3/dist/vue.global.prod.js";
const DEBOUNCE_MS = 400;
const PREVIEW_RECORD_BRIDGE_CHANNEL = "primum:service-builder-preview-record";

type PreviewRecordBridgeRequest = {
    channel?: string;
    previewId?: string;
    requestId?: string;
    method?: "record.query" | "record.get";
    payload?: any;
};

const DEFAULT_PREVIEW_TRANSLATION_LANGUAGES = ["en-US", "pt-BR"];

const mergeMessages = (base: Record<string, unknown>, extension: Record<string, unknown>): Record<string, unknown> => {
    return Object.entries(extension || {}).reduce<Record<string, unknown>>((accumulator, [key, value]) => {
        const currentValue = accumulator[key];

        if (
            value
            && typeof value === "object"
            && !Array.isArray(value)
            && currentValue
            && typeof currentValue === "object"
            && !Array.isArray(currentValue)
        ) {
            accumulator[key] = mergeMessages(
                currentValue as Record<string, unknown>,
                value as Record<string, unknown>
            );
            return accumulator;
        }

        accumulator[key] = value;
        return accumulator;
    }, { ...base });
};

const normalizeGateLocale = (value?: unknown) => {
    const rawValue = Array.isArray(value) ? value[0] : value;
    const normalizedValue = String(rawValue || "").trim();
    const lowerValue = normalizedValue.toLowerCase();

    if (lowerValue.startsWith("pt")) return "pt-BR";
    if (lowerValue.startsWith("en")) return "en-US";

    return normalizedValue || undefined;
};

const assignMessagePath = (target: Record<string, unknown>, path: string, value: string) => {
    const parts = path.split(".").map((part) => part.trim()).filter(Boolean);
    if (!parts.length) return;

    let cursor = target;

    parts.slice(0, -1).forEach((part) => {
        const current = cursor[part];

        if (!current || typeof current !== "object" || Array.isArray(current)) {
            cursor[part] = {};
        }

        cursor = cursor[part] as Record<string, unknown>;
    });

    cursor[parts[parts.length - 1]] = value;
};

const translationsToMessages = (records: TranslationRecord[], language: string) => {
    return records
        .filter((record) => record.language === language)
        .reduce<Record<string, unknown>>((messages, record) => {
            if (record.text_key && record.text_value !== undefined) {
                assignMessagePath(messages, record.text_key, record.text_value);
            }

            return messages;
        }, {});
};

const escapeScriptJson = (value: unknown) =>
    JSON.stringify(value).replace(/<\/script/gi, "<\\/script");

const escapeForInlineScript = (value: string) =>
    value
        .replace(/\\/g, "\\\\")
        .replace(/`/g, "\\`")
        .replace(/\$\{/g, "\\${")
        .replace(/<\/script>/gi, "<\\/script>");

const escapeForInlineStyle = (value: string) =>
    value.replace(/<\/style>/gi, "<\\/style>");

const cssVariablesToStyle = (variables: CssVariableMap) => `:root {
${Object.entries(variables)
    .map(([key, value]) => `  ${key}: ${String(value)};`)
    .join("\n")}
}`;

/** Memoized outside the component — static content that never changes */
const formRuntimeScript = escapeForInlineScript(buildFormRuntimeScript());
const formRuntimeStyles = escapeForInlineStyle(buildFormRuntimeStyles());

/**
 * Build the full preview HTML document that uses the real Gate form runtime
 * (Vue 3 + prm-* components + production CSS) inside a sandboxed iframe.
 */
const buildPreviewDocument = (html: string, styleCode = "", scriptCode = "", i18nPayload: PreviewI18nPayload, currencyPayload: GateRuntimeCurrencyPayload, previewBridgeId: string, themeCss = "", themePayload: Record<string, unknown> = {}) => `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  :root {
    --prm-primary: #3b82f6;
    --prm-primary-light: #60a5fa;
    --prm-primary-dark: #2563eb;
    --prm-primary-contrast: #ffffff;
    --prm-secondary: #6b7280;
    --prm-bg: #f7f8fa;
    --prm-surface: #ffffff;
    --prm-surface-raised: #ffffff;
    --prm-text: #1f2937;
    --prm-text-primary: #1f2937;
    --prm-text-secondary: #6b7280;
    --prm-text-disabled: #9ca3af;
    --prm-border: #e5e7eb;
    --prm-error: #ef4444;
    --prm-info: #3b82f6;
    --prm-success: #16a34a;
    --prm-warning: #d97706;
    --prm-focus: rgba(59, 130, 246, 0.15);
    --prm-radius: 6px;
    --prm-spacing: 8px;
  }
  html, body, #preview-app {
    width: 100%;
    height: 100%;
    margin: 0;
  }
  * { box-sizing: border-box; }
  body {
    font-family: Arial, sans-serif;
    background: var(--prm-bg);
    color: var(--prm-text);
  }
  [v-cloak] { display: none; }
  .preview-error {
    padding: 12px 16px;
    margin: 8px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    color: #991b1b;
    font-size: 13px;
    white-space: pre-wrap;
    font-family: monospace;
  }
  .preview-empty {
    padding: 24px;
    text-align: center;
    color: var(--prm-text-secondary);
    border: 1px dashed var(--prm-border);
    border-radius: 8px;
    margin: 8px;
  }
</style>
<style>${formRuntimeStyles}</style>
${themeCss ? `<style>${escapeForInlineStyle(themeCss)}</style>` : ""}
${styleCode ? `<style>${escapeForInlineStyle(styleCode)}</style>` : ""}
<script src="https://cdn.tailwindcss.com"></script>
<script>
  // cdn.tailwindcss.com replaces window.tailwind with its own reactive Proxy
  // (config starting empty) the instant this script tag runs, unconditionally
  // overwriting anything assigned to window.tailwind beforehand — so the config
  // must be assigned as a property *after* this script tag, not in a preceding
  // <script> (see the matching fix/comment in gateRuntimeHtml.ts, confirmed
  // empirically against both the vendored build and the live CDN before this fix).
  window.tailwind.config = {
    theme: {
      extend: {
        colors: {
          prm: {
            bg: "var(--prm-bg)",
            surface: "var(--prm-surface)",
            border: "var(--prm-border)",
            text: "var(--prm-text)",
            primary: "var(--prm-primary)",
            error: "var(--prm-error)"
          }
        },
        borderRadius: { prm: "var(--prm-radius, 6px)" },
        boxShadow: { prm: "var(--prm-shadow, 0 12px 30px rgba(15, 23, 42, 0.12))" }
      }
    }
  };
</script>
<script src="${VUE_CDN_URL}"></script>
</head>
<body>
  <div id="preview-app" v-cloak></div>

  <script>${formRuntimeScript}</script>
  <script type="application/json" id="preview-i18n-source">${escapeScriptJson(i18nPayload)}</script>
  <script type="application/json" id="preview-currency-source">${escapeScriptJson(currencyPayload)}</script>
  <script type="application/json" id="preview-theme-source">${escapeScriptJson(themePayload)}</script>

  <script>
    var previewI18nPayload = { locale: "en-US", fallbackLocale: "en-US", messages: {}, messagesByLocale: {} };
    var previewCurrencyPayload = { currencies: [], defaultCurrencyCode: null, defaultNumberFormat: null, userNumberFormat: null };
    var previewThemePayload = {};
    try {
      previewI18nPayload = JSON.parse(document.getElementById("preview-i18n-source").textContent || '{}');
    } catch(e) {}
    try {
      previewCurrencyPayload = JSON.parse(document.getElementById("preview-currency-source").textContent || '{}');
    } catch(e) {}
    try {
      previewThemePayload = JSON.parse(document.getElementById("preview-theme-source").textContent || '{}');
    } catch(e) {}

    var translateLookup = function(messages, key) {
      if (!messages || typeof messages !== "object" || !key) return undefined;
      return String(key).split(".").reduce(function(current, part) {
        if (!current || typeof current !== "object") return undefined;
        return current[part];
      }, messages);
    };
    var normalizeLocale = function(value) {
      var rawValue = Array.isArray(value) ? value[0] : value;
      var normalizedValue = String(rawValue || "").trim();
      var lowerValue = normalizedValue.toLowerCase();
      if (lowerValue.indexOf("pt") === 0) return "pt-BR";
      if (lowerValue.indexOf("en") === 0) return "en-US";
      return normalizedValue || undefined;
    };
    var readLocaleOption = function(localeOrOptions) {
      if (typeof localeOrOptions === "string") return normalizeLocale(localeOrOptions);
      if (localeOrOptions && typeof localeOrOptions === "object") return normalizeLocale(localeOrOptions.locale || localeOrOptions.language);
      return undefined;
    };
    var interpolateTranslation = function(value, params) {
      if (typeof value !== "string" || !params || typeof params !== "object") return value;
      return value.replace(/{{\\s*([^{}\\s]+)\\s*}}/g, function(_, token) {
        var replacement = params[token];
        return replacement === undefined || replacement === null ? "" : String(replacement);
      });
    };
    var gateI18n = {
      locale: previewI18nPayload.locale || "en-US",
      fallbackLocale: previewI18nPayload.fallbackLocale || "en-US",
      messages: previewI18nPayload.messages || {},
      messagesByLocale: previewI18nPayload.messagesByLocale || {},
      getMessages: function(localeOrOptions) {
        var requestedLocale = readLocaleOption(localeOrOptions);
        if (!requestedLocale || requestedLocale === this.locale) return this.messages;
        return this.messagesByLocale[requestedLocale] || {};
      },
      lookup: function(key, localeOrOptions) {
        var requestedLocale = readLocaleOption(localeOrOptions);
        if (!requestedLocale || requestedLocale === this.locale) return translateLookup(this.messages, key);
        var translated = translateLookup(this.messagesByLocale[requestedLocale] || {}, key);
        if (translated !== undefined) return translated;
        return translateLookup(this.messagesByLocale[this.fallbackLocale] || {}, key);
      },
      has: function(key, localeOrOptions) { return this.lookup(key, localeOrOptions) !== undefined; },
      t: function(key, params, fallback, localeOrOptions) {
        var translated = this.lookup(key, localeOrOptions);
        if (typeof translated === "string") return interpolateTranslation(translated, params);
        if (translated !== undefined && translated !== null) return translated;
        if (typeof fallback === "string") return interpolateTranslation(fallback, params);
        return key;
      }
    };


    var previewBridgeChannel = ${escapeScriptJson(PREVIEW_RECORD_BRIDGE_CHANNEL)};
    var previewBridgeId = ${escapeScriptJson(previewBridgeId)};
    var previewRecordRequests = {};
    var previewRecordRequestSeq = 0;

    window.addEventListener("message", function(event) {
      var message = event.data || {};
      if (!message || message.channel !== previewBridgeChannel || message.previewId !== previewBridgeId || !message.requestId) return;

      var pending = previewRecordRequests[message.requestId];
      if (!pending) return;

      delete previewRecordRequests[message.requestId];

      if (message.ok) {
        pending.resolve(message.payload);
      } else {
        pending.reject(new Error(message.error || "Preview record request failed"));
      }
    });

    var invokePreviewRecord = function(method, payload) {
      var requestId = String(++previewRecordRequestSeq);

      return new Promise(function(resolve, reject) {
        previewRecordRequests[requestId] = { resolve: resolve, reject: reject };

        window.parent.postMessage({
          channel: previewBridgeChannel,
          previewId: previewBridgeId,
          requestId: requestId,
          method: method,
          payload: payload || {}
        }, "*");

        setTimeout(function() {
          if (!previewRecordRequests[requestId]) return;
          delete previewRecordRequests[requestId];
          reject(new Error("Preview record request timed out"));
        }, 10000);
      });
    };

    // ── Gate mock for preview context ──
    window.Gate = {
      form: null,
      api: {
        auth: {
          getSession: function() { return Promise.resolve({ user: { id: "preview", name: "Preview User" } }); },
          login: function() { return Promise.resolve({}); },
          rememberLogin: function() { return Promise.resolve({}); },
          getRememberedLogin: function() { return Promise.resolve(null); },
          logout: function() { return Promise.resolve({}); }
        },
        record: {
          query: function(payload) {
            return invokePreviewRecord("record.query", payload).catch(function(error) {
              console.warn("[preview] record.query failed:", error);
              return { data: [], total: 0 };
            });
          },
          get: function(payload) {
            return invokePreviewRecord("record.get", payload).catch(function(error) {
              console.warn("[preview] record.get failed:", error);
              return null;
            });
          },
          create: function() { return Promise.resolve({ id: "preview-record" }); },
          update: function() { return Promise.resolve({}); },
          delete: function() { return Promise.resolve({}); }
        },
        email: {
          send: function() { return Promise.resolve({}); },
          sendTemplate: function() { return Promise.resolve({}); },
          renderTemplate: function() { return Promise.resolve({ html: "" }); },
          uploadAttachment: function() { return Promise.resolve({}); },
          deleteAttachment: function() { return Promise.resolve({}); }
        },
        attachments: {
          list: function() { return Promise.resolve({ data: [] }); },
          upload: function() { return Promise.resolve({}); },
          delete: function() { return Promise.resolve({}); },
          downloadUrl: function() { return Promise.resolve(""); },
          viewUrl: function() { return Promise.resolve(""); }
        },
        session: {
          createUser: function() { return Promise.resolve({}); },
          updateUser: function() { return Promise.resolve({}); },
          updatePassword: function() { return Promise.resolve({}); }
        }
      },
      router: {
        current: { pathname: "/preview", search: "", hash: "", params: {}, query: {} },
        get path() { return "/preview"; },
        get params() { return {}; },
        get query() { return {}; },
        navigate: function(to) { console.log("[preview] navigate:", to); return Promise.resolve(); }
      },
      ui: {
        notify: function(message, level) { console.log("[preview] notify:", level, message); return Promise.resolve(); }
      },
      modal: {
        data: {},
        submit: function() { return Promise.resolve(); },
        close: function() { return Promise.resolve(); },
        cancel: function() { return Promise.resolve(); }
      },
      i18n: gateI18n,
      currency: previewCurrencyPayload,
      theme: previewThemePayload,
      page: {}
    };
  </script>

  <script type="application/json" id="preview-html-source">${escapeScriptJson(html || "")}</script>
  <script type="application/json" id="preview-script-source">${escapeScriptJson(scriptCode || "")}</script>

  <script>
    (function() {
      var root = document.getElementById("preview-app");
      if (!root) return;

      var htmlSource = "";
      var scriptSource = "";

      try {
        htmlSource = JSON.parse(document.getElementById("preview-html-source").textContent || '""');
      } catch(e) {
        htmlSource = "";
      }
      try {
        scriptSource = JSON.parse(document.getElementById("preview-script-source").textContent || '""');
      } catch(e) {
        scriptSource = "";
      }

      if (!htmlSource || !htmlSource.trim()) {
        root.innerHTML = '<div class="preview-empty">Write a form template to see the preview.</div>';
        return;
      }

      // Check if template contains prm-form
      if (htmlSource.indexOf("prm-form") === -1) {
        root.innerHTML = '<div class="preview-empty">No &lt;prm-form&gt; element found in the template.</div>';
        return;
      }

      try {
        var formComponent = window.__prmCreateFormRuntimeComponent({
          template: htmlSource,
          script: scriptSource,
          catalog: null
        });

        if (!formComponent) {
          root.innerHTML = '<div class="preview-empty">Could not create form component from template.</div>';
          return;
        }

        var app = window.Vue.createApp(formComponent);
        app.config.globalProperties.$gate = window.Gate;
        app.config.globalProperties.$i18n = window.Gate.i18n;
        app.config.globalProperties.$t = window.Gate.i18n.t.bind(window.Gate.i18n);

        // Register all prm-* components
        if (window.__prmRegisterFormComponents) {
          window.__prmRegisterFormComponents(app);
        }

        // Suppress Vue warnings in preview context
        app.config.warnHandler = function() {};

        app.mount(root);
      } catch (error) {
        console.error("[FormPreview] Runtime error:", error);
        root.innerHTML = '<div class="preview-error">Preview Error:\\n' +
          String(error.message || error).replace(/</g, "&lt;").replace(/>/g, "&gt;") +
          '</div>';
      }
    })();
  </script>
</body>
</html>`;

const FormPreview = ({ html, styleCode = "", scriptCode = "", draftTranslations = {} }: FormPreviewProps) => {
    const { t, i18n: activeI18n } = useTranslation();
    const { data: sessionUser } = useSession();
    const { activeTheme } = usePlatformTheme();
    const currencyPreferences = useCurrencyPreferences();
    const [debouncedHtml, setDebouncedHtml] = useState(html);
    const [debouncedStyleCode, setDebouncedStyleCode] = useState(styleCode);
    const [debouncedScriptCode, setDebouncedScriptCode] = useState(scriptCode);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const previewBridgeId = useMemo(() => {
        const randomId = typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        return `service-builder-preview-${randomId}`;
    }, []);

    const appLocale = normalizeGateLocale(activeI18n.resolvedLanguage || activeI18n.language || i18n.resolvedLanguage || i18n.language);
    const browserLocale = normalizeGateLocale(window.navigator?.language);
    const locale = appLocale || browserLocale || "en-US";
    const fallbackLocale = Array.isArray(activeI18n.options?.fallbackLng)
        ? activeI18n.options.fallbackLng[0]
        : typeof activeI18n.options?.fallbackLng === "string"
            ? activeI18n.options.fallbackLng
            : "en-US";
    const normalizedFallbackLocale = normalizeGateLocale(fallbackLocale) || "en-US";
    const previewTranslationLanguages = useMemo(() => (
        Array.from(new Set([
            normalizedFallbackLocale,
            locale,
            ...DEFAULT_PREVIEW_TRANSLATION_LANGUAGES,
        ].filter(Boolean)))
    ), [locale, normalizedFallbackLocale]);

    const { data: previewTranslations = [] } = useQuery({
        queryKey: ["service_builder_preview_translations", previewTranslationLanguages],
        queryFn: async () => {
            const response = await new RecordManagement<TranslationRecord>("sys_translations")
                .where("language", "in", previewTranslationLanguages)
                .run();

            return response.data || [];
        },
        enabled: Boolean(previewTranslationLanguages.length),
        retry: false,
    });

    const previewI18n = useMemo<PreviewI18nPayload>(() => {
        const draftMessages: Record<string, unknown> = {};
        Object.entries(draftTranslations).forEach(([key, value]) => {
            if (value) assignMessagePath(draftMessages, key, value);
        });
        const globalMessages = activeI18n.getResourceBundle(locale, "translation")
            || i18n.getResourceBundle(locale, "translation")
            || {};
        const fallbackPublicMessages = translationsToMessages(previewTranslations, normalizedFallbackLocale);
        const localePublicMessages = translationsToMessages(previewTranslations, locale);
        const messages = [fallbackPublicMessages, globalMessages, localePublicMessages, draftMessages]
            .reduce<Record<string, unknown>>((merged, nextMessages) => mergeMessages(merged, nextMessages), {});
        const messagesByLocale = previewTranslationLanguages.reduce<Record<string, Record<string, unknown>>>((accumulator, language) => {
            const globalLanguageMessages = activeI18n.getResourceBundle(language, "translation")
                || i18n.getResourceBundle(language, "translation")
                || {};
            const translationMessages = translationsToMessages(previewTranslations, language);

            accumulator[language] = [globalLanguageMessages, translationMessages, ...(language === locale ? [draftMessages] : [])]
                .reduce<Record<string, unknown>>((merged, nextMessages) => mergeMessages(merged, nextMessages), {});

            return accumulator;
        }, {});

        return {
            locale,
            fallbackLocale: normalizedFallbackLocale,
            messages,
            messagesByLocale,
        };
    }, [activeI18n, locale, normalizedFallbackLocale, previewTranslationLanguages, previewTranslations, draftTranslations]);

    const previewCurrency = useMemo<GateRuntimeCurrencyPayload>(() => ({
        currencies: currencyPreferences.data.currencies,
        defaultCurrencyCode: currencyPreferences.data.defaultCurrencyCode ?? null,
        defaultNumberFormat: currencyPreferences.data.defaultNumberFormat ?? null,
        userNumberFormat: sessionUser?.numberFormat ?? null,
    }), [currencyPreferences.data, sessionUser?.numberFormat]);

    const previewThemeCss = useMemo(() => cssVariablesToStyle({
        "color-scheme": activeTheme.mode === "dark" ? "dark" : "light",
        ...platformThemeCssVariables(activeTheme),
    }), [activeTheme]);

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setDebouncedHtml(html);
            setDebouncedStyleCode(styleCode);
            setDebouncedScriptCode(scriptCode);
        }, DEBOUNCE_MS);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [html, styleCode, scriptCode]);

    useEffect(() => {
        const handlePreviewRecordRequest = async (event: MessageEvent<PreviewRecordBridgeRequest>) => {
            const message = event.data || {};

            if (message.channel !== PREVIEW_RECORD_BRIDGE_CHANNEL || message.previewId !== previewBridgeId || !message.requestId) {
                return;
            }

            const source = event.source as Window | null;
            const respond = (payload: unknown, ok = true) => {
                source?.postMessage({
                    channel: PREVIEW_RECORD_BRIDGE_CHANNEL,
                    previewId: previewBridgeId,
                    requestId: message.requestId,
                    ok,
                    payload: ok ? payload : undefined,
                    error: ok ? undefined : String(payload || "Preview record request failed"),
                }, "*");
            };

            try {
                if (message.method === "record.query") {
                    const payload = message.payload || {};
                    const manager = new RecordManagement(payload.table || "");

                    if (payload.query) {
                        manager.builderFromQuery(payload.query);
                    } else if (Array.isArray(payload.filters) && payload.filters.length > 0) {
                        manager.builderFromQuery({
                            type: "logical",
                            operator: "and",
                            conditions: payload.filters
                                .filter((filter: any) => filter && filter.field)
                                .map((filter: any) => ({
                                    type: "condition",
                                    field: filter.field,
                                    operator: filter.operator || "eq",
                                    value: filter.value,
                                })),
                        });
                    }

                    if (payload.referenceMode) {
                        manager.referenceMode(payload.referenceMode);
                    }

                    if (payload.limit) {
                        manager.limit(payload.limit);
                    }

                    respond(await manager.run());
                    return;
                }

                if (message.method === "record.get") {
                    const payload = message.payload || {};
                    respond(await new RecordManagement(payload.table || "").get(payload.id || ""));
                    return;
                }

                respond("Unsupported preview record method", false);
            } catch (error) {
                respond(error instanceof Error ? error.message : String(error), false);
            }
        };

        window.addEventListener("message", handlePreviewRecordRequest);

        return () => {
            window.removeEventListener("message", handlePreviewRecordRequest);
        };
    }, [previewBridgeId]);

    const srcDoc = useMemo(
        () => buildPreviewDocument(debouncedHtml, debouncedStyleCode, debouncedScriptCode, previewI18n, previewCurrency, previewBridgeId, previewThemeCss, activeTheme as unknown as Record<string, unknown>),
        [debouncedHtml, debouncedStyleCode, debouncedScriptCode, previewI18n, previewCurrency, previewBridgeId, previewThemeCss, activeTheme]
    );

    return (
        <Box className="service-builder__preview">
            <Typography className="service-builder__section-title">{t("service_builder.preview", { defaultValue: "Preview" })}</Typography>
            <iframe
                title="Service form preview"
                srcDoc={srcDoc}
                sandbox="allow-scripts allow-forms allow-popups allow-modals"
            />
        </Box>
    );
};

export default FormPreview;
